import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import type { To } from 'react-router';
import { LIMITS, type ConversationItem, type Message } from '@chatapp-inbox/shared';
import { isApiError, sendWhisper } from '../../api';
import { useSession } from '../../auth';
import { isNarrowLayout } from '../../layout/use-narrow-focus';
import { COUNTDOWN_FROM_MS, countdownPhase, useReplyWindow } from '../conversation';
import { displayName } from '../conversations';
import { useQuickReplies } from '../quick-replies';
import { useToday } from '../conversations/use-today';
import { ChatHeader } from './ChatHeader';
import {
  MAX_FILES_AT_ONCE,
  captionTarget,
  checkFile,
  previewUrlFor,
  revokePreview,
  type PendingFile,
} from './attachments';
import { Composer, composerPhase, outgoingText, type ComposerHandle } from './Composer';
import { MessageList, type MessageListHandle } from './MessageList';
import { ReplyWindowCountdown, ReplyWindowNotice } from './ReplyWindowNotice';
import { templatesAvailable, useTemplates } from '../templates';
import { SendTemplateDialog } from './SendTemplateDialog';
import { WriteNotice } from './WriteNotice';
import { ClosedNotice } from './ClosedNotice';
import { isProviderUnavailable } from './message-status';
import { localId, readOutbox, removeLocal, useOutbox, type LocalSend } from './outbox';
import { discardLocal, newClientId, refusalMessage, retryLocal, submitMessage } from './send';
import { buildTimeline } from './timeline';
import { addMessageToCache } from './messages-cache';
import { useChatMessages } from './use-chat-messages';
import { canRecordVoice, MAX_RECORD_SECONDS, useVoiceRecorder } from './voice-recorder';
import { prependToDraft, readDraft, useDraft } from './use-draft';
import { useMarkRead } from './use-mark-read';
import { useRetry } from './use-retry';
import { useWindowAnnouncements } from './use-window-announcements';
import styles from './ChatPane.module.css';

export interface ChatPaneProps {
  conversation: ConversationItem;
  /** Rota da lista de conversas (preserva o filtro). */
  listHref: To;
  /** Rota do painel de detalhes (só usada em layout médio/estreito, onde o painel é outra tela). */
  detailsHref: To;
  /** Os detalhes estão abertos: o nome do cliente (e o botão "Detalhes") passam a FECHÁ-LOS (`detailsHref` leva ao chat). */
  detailsOpen?: boolean;
}

const FALLBACK_LOAD_ERROR = 'Não foi possível carregar as mensagens. Tente de novo.';
const SERVER_DOWN_MESSAGE = 'O servidor está indisponível no momento. Tente de novo em instantes.';

/** O texto cru de um 5xx ("Erro interno") não diz o que fazer: troca por uma frase útil. */
function loadErrorMessage(error: unknown): string {
  if (!isApiError(error)) return FALLBACK_LOAD_ERROR;
  return error.status >= 500 ? SERVER_DOWN_MESSAGE : error.message;
}

/** A contagem regressiva da janela de resposta se atualiza a cada tanto (só com a aba visível). */
const COUNTDOWN_TICK_MS = 30_000;

/**
 * A janela de chat: cabeçalho, histórico (com envios em andamento) e compositor.
 *
 * O conteúdo é chaveado por usuário + conversa: trocar de conversa (ou a conta, por outra aba)
 * reinicia tudo, e nada da anterior (rolagem, texto digitado, erros) sobra na tela. O que precisa
 * sobreviver à troca (rascunho, envios em falha) mora no cache do QueryClient.
 */
export function ChatPane(props: ChatPaneProps): ReactElement | null {
  const session = useSession();
  const user = session.status === 'authenticated' ? session.user : null;
  // Identidade estável: a linha do tempo só é refeita quando quem usa realmente muda.
  const viewer = useMemo(
    () => (user ? { id: user.id, name: user.name, role: user.role } : null),
    [user],
  );
  if (!viewer) return null; // RequireAuth já garante
  return <ChatPaneView key={`${viewer.id}/${props.conversation.id}`} {...props} viewer={viewer} />;
}

function ChatPaneView({
  conversation,
  listHref,
  detailsHref,
  detailsOpen = false,
  viewer,
}: ChatPaneProps & {
  viewer: { id: string; name: string; role: 'admin' | 'agent' };
}): ReactElement {
  const queryClient = useQueryClient();
  const conversationId = conversation.id;
  const { canWrite } = conversation;
  const { query, messages } = useChatMessages(conversationId);
  const outbox = useOutbox(conversationId);
  const [text, setText] = useDraft(conversationId);
  // Sussurro: só a equipe vê (nunca vai ao cliente). Fica ligado até a pessoa desligar (ver `Composer`).
  const [whisperMode, setWhisperMode] = useState(false);
  const [whisperBusy, setWhisperBusy] = useState(false);
  const [whisperError, setWhisperError] = useState<string | null>(null);
  const whisperBusyRef = useRef(false);
  const voiceRecorder = useVoiceRecorder();
  const quick = useQuickReplies();
  const today = useToday();
  const { retry, retrying, errors, retriedAt } = useRetry(conversationId);
  // Janela de resposta (24 h da Meta): decidida pelo relógio local. Fechada, o aviso toma o lugar do
  // compositor; o rascunho continua no cache e volta quando ela reabre.
  const replyWindow = useReplyWindow(conversation, {
    tickMs: COUNTDOWN_TICK_MS,
    tickWithinMs: COUNTDOWN_FROM_MS,
  });
  const windowClosed = replyWindow.kind === 'closed';
  const windowAnnouncement = useWindowAnnouncements(replyWindow);

  const rootRef = useRef<HTMLElement>(null);
  const listRef = useRef<MessageListHandle>(null);
  const composerRef = useRef<ComposerHandle>(null);
  // Depois de "Tentar de novo" na carga inicial o botão some; o foco vai para a lista que o substitui.
  const [recovering, setRecovering] = useState(false);
  // Janela "Enviar modelo": fica aqui (e não no aviso) para sobreviver à janela de resposta reabrir.
  const [templateOpen, setTemplateOpen] = useState(false);
  const canSendTemplate = templatesAvailable(useTemplates().data);
  const closeTemplate = useCallback(() => setTemplateOpen(false), []);

  // Arquivos escolhidos e ainda não enviados. Moram só nesta tela (um arquivo não cabe no cache nem no
  // rascunho); ao enviar, passam para a fila de envios, que cuida das miniaturas dali em diante.
  const [files, setFilesState] = useState<readonly PendingFile[]>([]);
  const [fileErrors, setFileErrors] = useState<readonly string[]>([]);
  const [dragging, setDragging] = useState(false);
  // Os arquivos também ficam numa ref, que os manipuladores leem na hora (sem esperar a renderização).
  const filesRef = useRef<readonly PendingFile[]>([]);
  const setFiles = useCallback(
    (update: (current: readonly PendingFile[]) => readonly PendingFile[]) => {
      filesRef.current = update(filesRef.current);
      setFilesState(filesRef.current);
    },
    [],
  );
  useEffect(
    () => () => {
      // Saiu da conversa com arquivos por enviar: as miniaturas não ficam na memória.
      for (const f of filesRef.current) revokePreview(f.previewUrl);
    },
    [],
  );

  const { entries, settled } = useMemo(
    () => buildTimeline({ conversationId, server: messages, outbox, viewer, now: today }),
    [conversationId, messages, outbox, viewer, today],
  );

  // Envios "sem confirmação" que o servidor já lista: o aviso de falha não faz mais sentido.
  useEffect(() => {
    for (const clientId of settled) removeLocal(queryClient, conversationId, clientId);
  }, [settled, queryClient, conversationId]);

  useMarkRead({
    conversationId,
    // Quem pode escrever e abriu a conversa a viu: o número some e só volta se o cliente escrever de novo. O
    // contador é um só por conversa (não por pessoa): o administrador que abre a conversa de outra pessoa também o
    // zera. Quem só pode LER (atendente olhando uma conversa alheia) nunca mexe nele.
    canWrite,
    unreadCount: conversation.unreadCount,
    loaded: query.isSuccess,
    messages,
  });

  // Foco depois de devolver texto ao compositor ou descartar um envio: o compositor, ou a lista se
  // ele não existe agora (sem permissão ou com a janela fechada). O foco nunca fica no <body>.
  const focusComposerOrLog = useCallback(() => {
    if (composerRef.current) composerRef.current.focus();
    else listRef.current?.focusLog();
  }, []);

  // Foco do compositor. Ao ABRIR a conversa, em layout largo, ele recebe o foco (em layout
  // estreito o teclado virtual cobriria a conversa; a casca leva o foco ao <main>). Quando a
  // pessoa GANHA a permissão com a conversa aberta (assumiu), o compositor aparece no lugar do
  // botão que ela clicou: recebe o foco no layout largo e, no estreito, a lista de mensagens
  // (o foco nunca fica no <body>). Se o foco já estiver em outro campo (anotando nos detalhes,
  // por exemplo), não é roubado.
  const wasWritable = useRef<boolean | null>(null);
  useEffect(() => {
    const before = wasWritable.current;
    wasWritable.current = canWrite;
    if (!canWrite) return;
    const narrow = isNarrowLayout();
    if (before === null) {
      if (!narrow) composerRef.current?.focus();
    } else if (before === false) {
      const active = document.activeElement;
      const idle = !active || active === document.body || rootRef.current?.contains(active);
      if (!idle) return;
      // Com a janela de resposta fechada o compositor não existe: a lista recebe o foco.
      if (narrow) listRef.current?.focusLog();
      else focusComposerOrLog();
    }
  }, [canWrite, focusComposerOrLog]);

  // A janela de resposta fecha (ou reabre) com a conversa aberta e o compositor some (ou volta). Se a
  // pessoa estava nele, o foco foi para o <body>: vai para a lista de mensagens, onde fica
  // "estacionado". Na volta o compositor recebe o foco no layout largo (no estreito o teclado virtual
  // cobriria a conversa) se o foco estiver perdido ou ainda estacionado na lista. Foco que está em
  // outro lugar NÃO é mexido, e a mudança de `canWrite` tem o efeito acima.
  const wasClosed = useRef<boolean | null>(null);
  const parkedOnLog = useRef(false);
  useEffect(() => {
    const before = wasClosed.current;
    wasClosed.current = windowClosed;
    if (before === null || before === windowClosed || !canWrite) return;
    const active = document.activeElement;
    const lost = !active || active === document.body;
    if (windowClosed) {
      if (!lost) return;
      listRef.current?.focusLog();
      parkedOnLog.current = true;
      return;
    }
    const parked = parkedOnLog.current && listRef.current?.logHasFocus() === true;
    parkedOnLog.current = false;
    if (!lost && !parked) return;
    if (isNarrowLayout()) listRef.current?.focusLog();
    else composerRef.current?.focus();
  }, [windowClosed, canWrite]);

  const addFiles = useCallback(
    (chosen: readonly File[]) => {
      const problems: string[] = [];
      const accepted: PendingFile[] = [];
      const room = MAX_FILES_AT_ONCE - filesRef.current.length;
      for (const file of chosen) {
        const check = checkFile(file);
        if (!check.ok) {
          problems.push(check.message);
        } else if (accepted.length >= room) {
          problems.push(`Você pode anexar até ${MAX_FILES_AT_ONCE} arquivos de uma vez.`);
          break;
        } else {
          accepted.push({
            id: newClientId(),
            file,
            kind: check.kind,
            mimeType: check.mimeType,
            previewUrl: previewUrlFor(file, check.kind),
          });
        }
      }
      if (accepted.length > 0) setFiles((current) => [...current, ...accepted]);
      setFileErrors([...new Set(problems)]);
    },
    [setFiles],
  );

  const removeFile = useCallback(
    (id: string) => {
      const gone = filesRef.current.find((f) => f.id === id);
      if (gone) revokePreview(gone.previewUrl);
      setFiles((current) => current.filter((f) => f.id !== id));
      composerRef.current?.focus();
    },
    [setFiles],
  );

  const submitWhisper = useCallback(async () => {
    const draft = readDraft(queryClient, conversationId);
    const phase = composerPhase(draft, LIMITS.messageText);
    if (!canWrite || phase === 'over' || phase === 'empty' || whisperBusyRef.current) return;
    whisperBusyRef.current = true;
    setWhisperBusy(true);
    setWhisperError(null);
    try {
      const message = await sendWhisper({
        conversationId,
        text: outgoingText(draft),
        clientId: newClientId(),
      });
      addMessageToCache(queryClient, conversationId, message);
      // O campo só esvazia se a pessoa não escreveu mais nada enquanto o sussurro ia.
      if (readDraft(queryClient, conversationId) === draft) setText('');
      listRef.current?.stickToBottom();
    } catch (error) {
      setWhisperError(
        // Só o que o servidor RECUSOU tem texto próprio; rede e erro 5xx viram o aviso de "tente de novo".
        isApiError(error) && error.status >= 400 && error.status < 500
          ? refusalMessage(error)
          : 'Não foi possível enviar o sussurro agora. O texto continua no campo; tente de novo.',
      );
    } finally {
      whisperBusyRef.current = false;
      setWhisperBusy(false);
      composerRef.current?.focus();
    }
  }, [queryClient, conversationId, canWrite, setText]);

  const submit = useCallback(() => {
    if (whisperMode) {
      void submitWhisper();
      return;
    }
    // O rascunho do cache é a fonte da verdade (atualizado a cada tecla, de forma síncrona): um
    // Enter repetido antes da nova renderização já o encontra vazio e não envia de novo.
    const draft = readDraft(queryClient, conversationId);
    const chosen = filesRef.current;
    const limit = chosen.length > 0 ? LIMITS.caption : LIMITS.messageText;
    const phase = composerPhase(draft, limit);
    if (!canWrite || phase === 'over' || (phase === 'empty' && chosen.length === 0)) return;
    setText('');
    setFiles(() => []);
    setFileErrors([]);
    listRef.current?.stickToBottom();
    const caption = outgoingText(draft);
    // O texto vira a legenda do primeiro arquivo que aceita legenda (áudio não aceita). Se nenhum
    // aceita, o texto sai antes, sozinho, como uma mensagem comum.
    const target = captionTarget(chosen);
    if (caption !== '' && (chosen.length === 0 || target === -1)) {
      submitMessage(queryClient, conversationId, caption);
    }
    chosen.forEach((file, index) => {
      submitMessage(queryClient, conversationId, index === target ? caption : '', file);
    });
    composerRef.current?.focus();
  }, [queryClient, conversationId, canWrite, setText, setFiles, submitWhisper, whisperMode]);

  // Termina a gravação e manda a nota de voz: sobe (o servidor converte para OGG/Opus mono) e vai como mensagem de voz.
  const sendVoice = useCallback(async () => {
    const recorded = await voiceRecorder.stop();
    if (!recorded || !canWrite) return;
    const extension = recorded.mimeType.includes('ogg')
      ? 'ogg'
      : recorded.mimeType.includes('mp4')
        ? 'm4a'
        : 'webm';
    const file = new File([recorded.blob], `nota-de-voz.${extension}`, { type: recorded.mimeType });
    listRef.current?.stickToBottom();
    submitMessage(
      queryClient,
      conversationId,
      '',
      { file, kind: 'audio', mimeType: recorded.mimeType, previewUrl: null },
      { voice: true },
    );
    composerRef.current?.focus();
  }, [voiceRecorder, canWrite, queryClient, conversationId]);

  // Passou do tempo máximo: a gravação é enviada sozinha.
  useEffect(() => {
    if (voiceRecorder.status === 'recording' && voiceRecorder.seconds >= MAX_RECORD_SECONDS) {
      void sendVoice();
    }
  }, [voiceRecorder.status, voiceRecorder.seconds, sendVoice]);

  const onRetry = useCallback(
    (message: Message) => {
      listRef.current?.focusMessage(message.id); // o botão some ao reenviar
      // O servidor trata o reenvio como uma tentativa nova e a mensagem vai para o FIM da conversa:
      // a pessoa é levada até lá, onde "Enviando…" aparece (o foco acompanha o elemento: o React o
      // restaura depois de mover o nó).
      void retry(message).then((sent) => {
        if (sent) listRef.current?.stickToBottom();
      });
    },
    [retry],
  );

  const onRetryLocal = useCallback(
    (local: LocalSend) => {
      listRef.current?.focusMessage(localId(local.clientId));
      retryLocal(queryClient, conversationId, local.clientId);
    },
    [queryClient, conversationId],
  );

  const onEditLocal = useCallback(
    (local: LocalSend) => {
      discardLocal(queryClient, conversationId, local.clientId);
      setText(prependToDraft(local.text, readDraft(queryClient, conversationId)));
      // O arquivo volta ao compositor junto do texto (com uma miniatura nova: a anterior foi liberada).
      const back = local.attachment;
      if (back) {
        setFiles((current) => [
          ...current,
          {
            id: newClientId(),
            file: back.file,
            kind: back.kind,
            mimeType: back.mimeType,
            previewUrl: previewUrlFor(back.file, back.kind),
          },
        ]);
      }
      focusComposerOrLog();
    },
    [queryClient, conversationId, setText, setFiles, focusComposerOrLog],
  );

  const onDiscardLocal = useCallback(
    (local: LocalSend) => {
      // Só a existência na fila importa aqui: um duplo clique não devolve o texto duas vezes.
      if (!readOutbox(queryClient, conversationId).some((l) => l.clientId === local.clientId)) {
        return;
      }
      discardLocal(queryClient, conversationId, local.clientId);
      // Devolve o texto para a pessoa não perdê-lo, mas nunca por cima do que ela já digitou.
      if (!readDraft(queryClient, conversationId).trim()) setText(local.text);
      focusComposerOrLog();
    },
    [queryClient, conversationId, setText, focusComposerOrLog],
  );

  const { fetchNextPage } = query;
  const loadOlder = useCallback(() => {
    // `cancelRefetch: false`: duas interseções seguidas não viram duas buscas.
    void fetchNextPage({ cancelRefetch: false });
  }, [fetchNextPage]);

  // Anúncio das falhas de envio. O texto é derivado dos envios em falha (não há estado a mais) e
  // a região existe desde o início: quando o texto muda ela é anunciada com educação.
  const failedSends = outbox.filter(
    (l) => l.state === 'unconfirmed' || l.state === 'rejected',
  ).length;
  const failureNotice =
    failedSends === 0
      ? ''
      : failedSends === 1
        ? 'Não foi possível enviar a mensagem.'
        : `Não foi possível enviar ${failedSends} mensagens.`;

  const loadFailed = query.data === undefined && query.isError;
  const loading = query.data === undefined && !loadFailed;
  // Arquivos só se anexam onde há compositor: com permissão e com a janela de resposta aberta.
  const acceptsFiles = canWrite && !windowClosed && !loadFailed;

  return (
    <section
      ref={rootRef}
      aria-label="Conversa"
      className={styles.pane}
      onDragOver={(event) => {
        // Só arrastar ARQUIVOS (texto ou links soltos não são anexos) e só onde dá para responder.
        if (!acceptsFiles || !event.dataTransfer.types.includes('Files')) return;
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(event) => {
        // `dragleave` também dispara ao passar por elementos filhos: só vale ao sair da seção.
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false);
      }}
      onDrop={(event) => {
        setDragging(false);
        if (!acceptsFiles || event.dataTransfer.files.length === 0) return;
        event.preventDefault();
        addFiles(Array.from(event.dataTransfer.files));
      }}
    >
      <ChatHeader
        conversation={conversation}
        listHref={listHref}
        detailsHref={detailsHref}
        detailsOpen={detailsOpen}
      />

      {query.data === undefined ? (
        <div className={styles.state}>
          {/* As duas regiões existem antes do texto, para o leitor de tela anunciá-lo. */}
          <div role="status">
            {loading && <p className={styles.muted}>Carregando mensagens…</p>}
          </div>
          <div role="alert">
            {loadFailed && <p className={styles.error}>{loadErrorMessage(query.error)}</p>}
          </div>
          {loadFailed && (
            <button
              type="button"
              className={styles.retry}
              onClick={() => {
                setRecovering(true);
                void query.refetch();
              }}
            >
              Tentar de novo
            </button>
          )}
        </div>
      ) : (
        <MessageList
          ref={listRef}
          entries={entries}
          messages={messages}
          viewerId={viewer.id}
          contactName={displayName(conversation.contact)}
          canWrite={canWrite}
          unavailable={isProviderUnavailable(messages)}
          hasOlder={query.hasNextPage}
          loadingOlder={query.isFetchingNextPage}
          olderFailed={query.isFetchNextPageError}
          onLoadOlder={loadOlder}
          focusOnMount={recovering}
          retrying={retrying}
          retryErrors={errors}
          retriedAt={retriedAt}
          onRetry={onRetry}
          onRetryLocal={onRetryLocal}
          onEditLocal={onEditLocal}
          onDiscardLocal={onDiscardLocal}
        />
      )}

      <div role="status" aria-label="Avisos de envio" className="sr-only">
        {failureNotice}
      </div>

      {/* Anúncio da janela de resposta: a região existe antes do texto, para o leitor de tela anunciá-lo. */}
      <div role="status" aria-label="Aviso da janela de resposta" className="sr-only">
        {windowAnnouncement.text && (
          <span key={windowAnnouncement.id}>{windowAnnouncement.text}</span>
        )}
      </div>

      {canWrite && conversation.status === 'closed' && <ClosedNotice />}

      {!canWrite ? (
        <WriteNotice conversation={conversation} />
      ) : windowClosed ? (
        <ReplyWindowNotice
          conversation={conversation}
          refusal={replyWindow.refusal}
          onSendTemplate={() => setTemplateOpen(true)}
        />
      ) : (
        <>
          {whisperError && (
            <p role="alert" className={styles.whisperError}>
              {whisperError}
            </p>
          )}
          <Composer
            ref={composerRef}
            value={text}
            onChange={setText}
            onSubmit={submit}
            // Se o histórico FALHOU em carregar, um envio não apareceria em lugar nenhum (a lista de
            // envios locais só é desenhada junto da lista): o texto sairia do campo e a pessoa não
            // veria a bolha nem a falha. Enquanto apenas CARREGA, o envio já pode sair: a bolha
            // aparece quando a lista chega.
            disabled={loadFailed}
            files={files}
            onAttach={addFiles}
            onRemoveFile={removeFile}
            fileErrors={fileErrors}
            onDismissFileErrors={() => setFileErrors([])}
            // O modelo vale com a janela aberta ou fechada, e fica na MESMA conversa (e no mesmo chat do Bitrix).
            {...(canSendTemplate ? { onSendTemplate: () => setTemplateOpen(true) } : {})}
            {...(canRecordVoice()
              ? {
                  voice: {
                    status: voiceRecorder.status,
                    seconds: voiceRecorder.seconds,
                    error: voiceRecorder.error,
                    onStart: () => void voiceRecorder.start(),
                    onSend: () => void sendVoice(),
                    onCancel: voiceRecorder.cancel,
                    onDismissError: voiceRecorder.dismissError,
                  },
                }
              : {})}
            whisper={{
              active: whisperMode,
              busy: whisperBusy,
              onToggle: () => {
                setWhisperError(null);
                setWhisperMode((on) => !on);
                composerRef.current?.focus();
              },
            }}
            quickReplies={{
              items: quick.data ?? [],
              state: quick.data ? 'ready' : quick.isError ? 'error' : 'loading',
              canManage: viewer.role === 'admin',
              clientName: conversation.contact.name,
              agentName: viewer.name,
              onRetry: () => void quick.refetch(),
              // Abrir o painel atualiza a lista se ela já passou de 1 minuto.
              onOpen: () => {
                if (quick.isStale || quick.isError) void quick.refetch();
              },
            }}
            hint={
              replyWindow.kind === 'open' && replyWindow.remainingMs <= COUNTDOWN_FROM_MS ? (
                <ReplyWindowCountdown
                  remainingMs={replyWindow.remainingMs}
                  phase={countdownPhase(replyWindow.remainingMs)}
                />
              ) : undefined
            }
          />
        </>
      )}

      {templateOpen && (
        <SendTemplateDialog
          conversationId={conversationId}
          lineId={conversation.line?.id}
          windowClosed={windowClosed}
          onClose={closeTemplate}
        />
      )}

      {dragging && (
        <div className={styles.dropOverlay} aria-hidden="true">
          Solte os arquivos para anexar
        </div>
      )}
    </section>
  );
}
